<template>
    <UContainer class="min-h-screen-with-header my-8" key="contact_page">
        <div class="flex items-start gap-4 max-lg:flex-wrap py-8 lg:py-16">
            <div class="lg:w-1/2 p-6 lg:p-12">
                <UiComponentTypography tag="h2" variant="h1" class-name="text-amazon-500 underline">
                    با ما در ارتباط باشید
                </UiComponentTypography>
                <UiComponentTypography class-name="mt-6 pr-4 text-gray-500">
                    همکاران ما در مجموعه نرس لند همواره پذیرای نظرات و درخواست‌های شما هستند. با ثبت نظرات، انتقادات و
                    پیشنهادات خود می‌توانید ما را در افزایش کیفیت خدمات خود به شما یاری نمایید.
                </UiComponentTypography>
                <div class="max-w-72 mx-auto mt-8">
                    <ClientOnly>
                        <DotLottieVue style="width: 100%" autoplay loop src="/lottie/contact-us-animation.lottie" />
                        <!-- <Vue3Lottie :animationData="contactUsAnimation" width="100%" /> -->
                    </ClientOnly>
                </div>
            </div>
            <div class="bg-white shadow-73 p-6 rounded-md lg:w-1/2">
                <PagesContactForm />
            </div>
        </div>
        <div class="mt-12">
            <ul class="flex gap-6 lg:gap-12 justify-between max-lg:flex-wrap h-full">
                <li class="w-full lg:w-1/3 group cursor-default" v-for="(item, index) in contactList" :key="index">
                    <div class="w-full h-full flex bg-white shadow-37 rounded-lg overflow-hidden">
                        <div class="w-3/4 p-4 pl-1 relative before:absolute card-list-item-description">
                            <UiComponentTypography tag="h4"
                                class-name="group-hover:opacity-0 group-hover:-translate-y-3 transition-all duration-300 ease-in-out">
                                {{ item.title }}
                            </UiComponentTypography>
                            <div v-html="item.description"
                                class="mt-2 text-gray-600 transition-all duration-300 ease-in-out group-hover:-translate-y-8">
                            </div>
                        </div>
                        <div
                            class="w-1/4 flex justify-center items-center relative overflow-hidden before:absolute contact-list-item-icon">
                            <UIcon :name="item.icon"
                                class="w-12 h-12 text-amazon-500 group-hover:text-white relative z-10" />
                        </div>
                    </div>
                </li>
            </ul>
        </div>
        <div class="mt-12 rounded-md overflow-hidden">
            <ClientOnly>
                <LMap style="height: 30vh" :zoom="16" :center="[36.295045, 59.577152]" :use-global-leaflet="false">
                    <LTileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" layer-type="base"
                        name="OpenStreetMap" />
                    <LMarker :lat-lng="[36.2952, 59.5772]" />
                </LMap>
            </ClientOnly>
        </div>
    </UContainer>
</template>

<script lang="ts" setup>
const DotLottieVue = defineAsyncComponent(() =>
    import("@lottiefiles/dotlottie-vue").then((m) => m.DotLottieVue)
);

useSeoMeta({
    title: "تماس با ما | شرکت رستری قهوه نرس‌لند",
    description: "راه‌های ارتباطی، آدرس دفتر مرکزی در مشهد، شماره‌های تماس، صدای مشتری و ساعات کاری مجموعه رستری قهوه نرس‌لند.",
    canonical: "https://northland-co.com/contact",
    ogTitle: "تماس با ما | نرس‌لند",
    ogDescription: "راه‌های ارتباط با کارشناسان و پشتیبانی رستری قهوه نرس‌لند.",
    ogType: "website",
    ogUrl: "https://northland-co.com/contact",
    robots: "index, follow",
});

useSchemaOrg([
    defineLocalBusiness({
        name: "Northland Co. | نرس‌لند",
        image: "https://northland-co.com/Images/logo/logo.png",
        telephone: "+985138411001",
        address: {
            streetAddress: "خیابان احمدآباد، احمدآباد 9 (خیابان نشاط)، ساختمان زمرد، واحد 101",
            addressLocality: "مشهد",
            addressRegion: "خراسان رضوی",
            addressCountry: "IR",
        },
        geo: {
            latitude: 36.2952,
            longitude: 59.5772,
        },
        openingHoursSpecification: [
            {
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Saturday"],
                opens: "08:30",
                closes: "15:30",
            },
        ],
    }),
    defineBreadcrumb({
        itemListElement: [
            { name: "خانه", item: "/" },
            { name: "تماس با ما", item: "https://northland-co.com/contact" },
        ],
    }),
]);

defineOgImageComponent("Main", {
    title: "تماس با ما",
    description: "راه‌های ارتباطی و آدرس دفتر مرکزی شرکت نرس‌لند",
    path: "/contact",
});

const contactList = [
    {
        title: "آدرس دفتر مرکزی",
        description: "<p>خراسان رضوی، مشهد، خیابان احمدآباد، احمدآباد 9 (خیابان نشاط)، ساختمان زمرد، واحد 101</p>",
        icon: "i-solar-streets-map-point-line-duotone",
    },
    {
        title: "تماس",
        description:
            "<div class='flex gap-2'><p class='w-1/3'><b>دفتر: </b></p><div><p class='tracking-widest'>38411001 - 051</p><p class='tracking-widest'>38484001</p></div></div><p class='mt-4 flex gap-2'><b class='w-1/3 whitespace-nowrap'>صدای مشتری:</b> <span class='tracking-widest'>09151182005</span></p>",
        icon: "i-solar-phone-calling-rounded-line-duotone",
    },
    {
        title: "ساعات کاری",
        description: "<p class='mt-4'>شنبه تا پنج‌شنبه: 8:30 الی 15:30</p>",
        icon: "i-solar-clock-circle-line-duotone",
    },
];
</script>

<style scoped>
.contact-list-item-icon::before {
    @apply top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0 h-0 bg-gradient-to-tr from-amazon-500 via-amazon-500 to-amazon-300 transition-all duration-300 ease-in-out rounded-full z-0;
}

.group:hover .contact-list-item-icon::before {
    width: 200%;
    height: 200%;
}

.card-list-item-description::before {
    @apply inset-0 translate-y-1/2 transition-all duration-300 ease-in-out;
    background-color: transparent;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='198'%3e%3cdefs%3e%3clinearGradient id='a' x1='50%25' x2='50%25' y1='-10.959%25' y2='100%25'%3e%3cstop stop-color='%23438957' stop-opacity='.25' offset='0%25'/%3e%3cstop stop-color='%23438957' offset='100%25'/%3e%3c/linearGradient%3e%3c/defs%3e%3cpath fill='url(%23a)' fill-rule='evenodd' d='M.005 121C311 121 409.898-.25 811 0c400 0 500 121 789 121v77H0s.005-48 .005-77z'/%3e%3c/svg%3e"),
        url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='198'%3e%3cdefs%3e%3clinearGradient id='a' x1='50%25' x2='50%25' y1='-10.959%25' y2='100%25'%3e%3cstop stop-color='%23438957' stop-opacity='.25' offset='0%25'/%3e%3cstop stop-color='%23438957' offset='100%25'/%3e%3c/linearGradient%3e%3c/defs%3e%3cpath fill='url(%23a)' fill-rule='evenodd' d='M.005 121C311 121 409.898-.25 811 0c400 0 500 121 789 121v77H0s.005-48 .005-77z'/%3e%3c/svg%3e"),
        url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='198'%3e%3cdefs%3e%3clinearGradient id='a' x1='50%25' x2='50%25' y1='-10.959%25' y2='100%25'%3e%3cstop stop-color='%23438957' stop-opacity='.25' offset='0%25'/%3e%3cstop stop-color='%23438957' offset='100%25'/%3e%3c/linearGradient%3e%3c/defs%3e%3cpath fill='url(%23a)' fill-rule='evenodd' d='M.005 121C311 121 409.898-.25 811 0c400 0 500 121 789 121v77H0s.005-48 .005-77z'/%3e%3c/svg%3e");
    background-repeat: repeat-x;
    background-size: 1600px 50%;
    background-position: 0 130%, -50px 130%, 500px 130%;

    animation: 20s waves linear infinite forwards;
}

.group:hover .card-list-item-description::before {
    @apply translate-y-0;
}

@keyframes waves {
    to {
        background-position: 1600px 130%, 3150px 130%, 5300px 130%;
    }
}
</style>
